import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation, Trans } from 'react-i18next';

const BAPU_PAGE_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://www.shreeramranujadham.org/shree-radhe-krishna-bapu#webpage",
      "url": "https://www.shreeramranujadham.org/shree-radhe-krishna-bapu",
      "name": "Shree Radhe Krishna Bapu | Biography, Katha & Ashram",
      "isPartOf": { "@id": "https://www.shreeramranujadham.org/#website" },
      "mainEntity": { "@id": "https://www.shreeramranujadham.org/#bapu" }
    },
    {
      "@type": "Person",
      "@id": "https://www.shreeramranujadham.org/#bapu",
      "name": "Shree Radhe Krishna Bapu",
      "honorificPrefix": "Pujya Mahant",
      "alternateName": [
        "Shree Radhekrishna Shastri Bapu",
        "Shri Radhekrishna Shastri Bapu",
        "Shree Radhekrishna Bapu",
        "Radhe Krishna Bapu",
        "Radhekrishna Bapu",
        "Radhekrishna Shastri Bapu",
        "Radhe Krishna Shastri Bapu",
        "Radhe Krishna Shastri",
        "Pujya Mahant Radhekrishna Bapu",
        "Radhekrishna Bapu Katha",
        "Radhekrishna Bapu Live",
        "Radhekrishna Bapu Ashram",
        "Radhekrishna Bapu Rampar",
        "શ્રી રાધેકૃષ્ણ શાસ્ત્રી બાપુ",
        "શ્રી રાધેકૃષ્ણ બાપુ",
        "રાધેકૃષ્ણ બાપુ",
        "રાધે કૃષ્ણ બાપુ",
        "રાધેકૃષ્ણ શાસ્ત્રી",
        "રાધેકૃષ્ણ બાપુ કથા",
        "રાધેકૃષ્ણ બાપુ લાઈવ",
        "Shri Radhe Krishna Bapu",
        "Radhekrishna Bapu Live Katha",
        "Radhekrishna Bapu Satsang",
        "Radhekrishna Bapu Official",
        "શ્રી રાધે કૃષ્ણ શાસ્ત્રી બાપુ",
        "શ્રી રાધે કૃષ્ણ બાપુ",
        "રાધેકૃષ્ણ બાપુ સત્સંગ",
        "રાધેકૃષ્ણ બાપુ ઓફિશિયલ",
        "રાધેકૃષ્ણ બાપુ લાઈવ કથા",
        "श्री राधेकृष्ण शास्त्री बापू",
        "श्री राधेकृष्ण बापू",
        "राधेकृष्ण बापू",
        "राधेकृष्ण बापू कथा"
      ],
      "url": "https://www.shreeramranujadham.org/shree-radhe-krishna-bapu",
      "description": "Pujya Mahant Shree Radhekrishna Shastri Bapu is a spiritual leader and Kathakar associated with Shree Ram Ranuja Dham Ashram (Ram Ranuja Dham) in Rampar, Lathi, Amreli, Gujarat.",
      "sameAs": [
        "https://www.youtube.com/@Radhekrishnabapuofficial",
        "https://www.facebook.com/radhekrishnashastri.bapu",
        "https://www.instagram.com/radhekrishnabapu_official"
      ],
      "affiliation": { "@id": "https://www.shreeramranujadham.org/#organization" }
    }
  ]
};

const PRINCIPLE_META = [
  {
    key: 'satya',
    icon: 'fas fa-sun',
    cardStyle: { background: 'linear-gradient(to bottom right, #ffffff, #fff7ed)', borderColor: '#ffedd5' },
    cardHoverBorder: '#fdba74',
    iconStyle: { background: 'linear-gradient(to bottom right, #fed7aa, #fef08a)', borderColor: '#fdba74', color: '#ea580c' },
    hoverOverlayStyle: { background: 'linear-gradient(to bottom right, rgba(255, 237, 213, 0.5), rgba(254, 249, 195, 0.5))' },
    badgeStyle: { color: '#ea580c', background: '#ffedd5' },
    titleHover: '#c2410c',
  },
  {
    key: 'dharma',
    icon: 'fas fa-balance-scale',
    cardStyle: { background: 'linear-gradient(to bottom right, #ffffff, #f0fdf4)', borderColor: '#dcfce7' },
    cardHoverBorder: '#86efac',
    iconStyle: { background: 'linear-gradient(to bottom right, #bbf7d0, #a7f3d0)', borderColor: '#86efac', color: '#16a34a' },
    hoverOverlayStyle: { background: 'linear-gradient(to bottom right, rgba(220, 252, 231, 0.5), rgba(209, 250, 229, 0.5))' },
    badgeStyle: { color: '#16a34a', background: '#dcfce7' },
    titleHover: '#15803d',
  },
  {
    key: 'bhakti',
    icon: 'fas fa-heart',
    cardStyle: { background: 'linear-gradient(to bottom right, #ffffff, #faf5ff)', borderColor: '#f3e8ff' },
    cardHoverBorder: '#d8b4fe',
    iconStyle: { background: 'linear-gradient(to bottom right, #e9d5ff, #c7d2fe)', borderColor: '#d8b4fe', color: '#9333ea' },
    hoverOverlayStyle: { background: 'linear-gradient(to bottom right, rgba(243, 232, 255, 0.5), rgba(224, 231, 255, 0.5))' },
    badgeStyle: { color: '#9333ea', background: '#f3e8ff' },
    titleHover: '#7e22ce',
  },
];

const TIMELINE_META = [
  { key: 'birth', icon: 'fas fa-baby', iconGradient: 'linear-gradient(to right, #f97316, #ef4444)', border: 'border-orange-100', badgeIcon: 'fas fa-star', left: true },
  { key: 'learning', icon: 'fas fa-book-open', iconGradient: 'linear-gradient(to right, #22c55e, #10b981)', border: 'border-green-100', badgeIcon: 'fas fa-book', left: false },
  { key: 'mission', icon: 'fas fa-microphone', iconGradient: 'linear-gradient(to right, #a855f7, #6366f1)', border: 'border-purple-100', badgeIcon: 'fas fa-microphone', left: true },
  { key: 'sayla', icon: 'fas fa-home', iconGradient: 'linear-gradient(to right, #eab308, #f97316)', border: 'border-yellow-100', badgeIcon: 'fas fa-building', left: false },
  { key: 'expansion', icon: 'fas fa-globe', iconGradient: 'linear-gradient(to right, #3b82f6, #06b6d4)', border: 'border-blue-100', badgeIcon: 'fas fa-globe', left: true },
  { key: 'rampar', icon: 'fas fa-place-of-worship', iconGradient: 'linear-gradient(to right, #ef4444, #ec4899)', border: 'border-red-100', badgeIcon: 'fas fa-place-of-worship', left: false },
  { key: 'present', icon: 'fas fa-heart', iconGradient: 'linear-gradient(to right, #f97316, #eab308)', border: 'border-orange-200', badgeIcon: 'fas fa-infinity', left: true, animatePulse: true, presentBg: true },
];

const Bold = <span className="font-semibold text-gray-800" />;

const About = () => {
  const { t } = useTranslation();

  const principles = useMemo(
    () => PRINCIPLE_META.map((p) => ({
      ...p,
      title: t(`aboutPage.principles.${p.key}.title`),
      desc: t(`aboutPage.principles.${p.key}.desc`),
      badge: t(`aboutPage.principles.${p.key}.badge`),
    })),
    [t]
  );

  const timeline = useMemo(
    () => TIMELINE_META.map((item) => ({
      ...item,
      year: t(`aboutPage.timeline.items.${item.key}.year`),
      title: t(`aboutPage.timeline.items.${item.key}.title`),
      desc: t(`aboutPage.timeline.items.${item.key}.desc`),
      badge: t(`aboutPage.timeline.items.${item.key}.badge`),
    })),
    [t]
  );

  const heroSanskrit = t('aboutPage.hero.sanskrit').split('\n');
  const missionSanskrit = t('aboutPage.mission.sanskrit').split('\n');

  return (
    <>
      <Helmet>
        <title>{t('aboutPage.meta.title')}</title>
        <meta name="description" content={t('aboutPage.meta.description')} />
        <link rel="canonical" href="https://www.shreeramranujadham.org/shree-radhe-krishna-bapu" />
        <meta property="og:title" content={t('aboutPage.meta.title')} />
        <meta property="og:description" content={t('aboutPage.meta.description')} />
        <meta property="og:url" content="https://www.shreeramranujadham.org/shree-radhe-krishna-bapu" />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
        <script type="application/ld+json">{JSON.stringify(BAPU_PAGE_JSONLD)}</script>
      </Helmet>

      {/* ── Page Header ─────────────────────────────────── */}
      <section
        className="pt-24 pb-6 md:pt-28 md:pb-16 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 50%, #7c3aed 100%)' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-20 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-20 rounded-full translate-y-40 -translate-x-40"></div>
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-400 bg-opacity-10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-yellow-400 bg-opacity-10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="mb-2 md:mb-8">
            <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
              <i className="fas fa-spa text-yellow-300 mr-3 text-xl"></i>
              <span className="text-white font-semibold">{t('aboutPage.hero.badge')}</span>
            </div>
          </div>
          <h1 className="text-2xl md:text-7xl lg:text-8xl font-bold text-white mb-3 md:mb-8 leading-tight drop-shadow-lg">
            {t('aboutPage.hero.title_1')}<br />
            <span className="text-yellow-300">{t('aboutPage.hero.title_2')}</span>
          </h1>
          <p className="text-xs md:text-xl text-white max-w-3xl mx-auto leading-relaxed mb-3 md:mb-8 drop-shadow-md font-medium">
            {t('aboutPage.hero.subtitle')}
          </p>

          <div className="max-w-2xl mx-auto bg-black bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-30 shadow-2xl">
            <blockquote className="text-xl text-white font-semibold italic leading-relaxed mb-4">
              {heroSanskrit.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < heroSanskrit.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </blockquote>
            <div className="w-16 h-px bg-yellow-300 mx-auto mb-4"></div>
            <p className="text-base text-white font-medium leading-relaxed">
              {t('aboutPage.hero.quote')}
            </p>
          </div>
        </div>
      </section>

      {/* ── Biography Section ─────────────────────────── */}
      <section className="pt-8 pb-0 md:pt-24 md:pb-8 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-20 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-20 rounded-full translate-y-40 -translate-x-40"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
                <i className="fas fa-user-circle text-orange-600 mr-3 text-lg"></i>
                <span className="text-orange-700 font-semibold">{t('aboutPage.bio.badge')}</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-gray-800 mb-8 leading-tight">
                {t('aboutPage.bio.title_1')}<br />
                <span className="text-orange-500 font-bold">{t('aboutPage.bio.title_2')}</span>
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 mb-16 about-bio-grid">
              <div className="order-1 about-bapu-col mb-8 lg:mb-0">
                <div className="relative about-bapu-photo-wrap">
                  <img
                    src={process.env.PUBLIC_URL + '/img-bapu.png'}
                    alt={t('aboutPage.bio.photo_alt')}
                    className="about-bapu-photo rounded-3xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl border border-orange-100">
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-500">50+</div>
                        <div className="text-xs text-gray-500">{t('aboutPage.bio.stat_years')}</div>
                      </div>
                      <div className="w-px h-8 bg-gray-200"></div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-500">950+</div>
                        <div className="text-xs text-gray-500">{t('aboutPage.bio.stat_kathas')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-2">
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-orange-100 h-full">
                  <h3 className="text-3xl font-bold text-gray-800 mb-2">{t('aboutPage.bio.name')}</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    <Trans
                      i18nKey="aboutPage.bio.p1"
                      components={[Bold, Bold, Bold]}
                    />
                  </p>

                  <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border-l-4 border-orange-500 mb-6">
                    <blockquote className="text-lg text-gray-700 italic font-medium mb-3">
                      {t('aboutPage.bio.sanskrit')}
                    </blockquote>
                    <p className="text-sm text-gray-600">
                      {t('aboutPage.bio.sanskrit_meaning')}
                    </p>
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-4">
                    <Trans i18nKey="aboutPage.bio.p2" components={[Bold]} />
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    <Trans i18nKey="aboutPage.bio.p3" components={[Bold]} />
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <Trans i18nKey="aboutPage.bio.p4" components={[Bold, Bold]} />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission Section ───────────────────────────── */}
      <section className="pt-12 pb-12 md:pt-16 md:pb-24 bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-orange-300 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-yellow-300 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-red-300 rounded-full blur-3xl"></div>
        </div>

        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundImage: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><text y=\"50\" font-size=\"30\" fill=\"%23FF6B35\">🪷</text></svg>')", backgroundSize: '100px 100px' }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
              <i className="fas fa-om text-orange-600 mr-3 text-lg"></i>
              <span className="text-orange-700 font-semibold">{t('aboutPage.mission.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-8 leading-tight">
              {t('aboutPage.mission.title_1')} <span className="text-orange-600 font-bold">{t('aboutPage.mission.title_2')}</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-700 max-w-6xl mx-auto leading-relaxed font-medium mb-6">
              {t('aboutPage.mission.subtitle')}
            </p>
            <div className="max-w-4xl mx-auto bg-white bg-opacity-80 rounded-2xl p-6 shadow-lg border border-orange-200">
              <blockquote className="text-xl text-gray-700 italic font-medium mb-3">
                {missionSanskrit.map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < missionSanskrit.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))}
              </blockquote>
              <p className="text-sm text-gray-600">
                {t('aboutPage.mission.sanskrit_meaning')}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {principles.map((p, i) => (
              <div
                key={p.key}
                className="group rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 hover:-translate-y-1 relative overflow-hidden"
                style={p.cardStyle}
                onMouseEnter={(e) => {
                  if (p.cardHoverBorder) e.currentTarget.style.borderColor = p.cardHoverBorder;
                  if (p.titleHover) {
                    const title = e.currentTarget.querySelector('h3');
                    if (title) title.style.color = p.titleHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (p.cardStyle?.borderColor) e.currentTarget.style.borderColor = p.cardStyle.borderColor;
                  if (p.titleHover) {
                    const title = e.currentTarget.querySelector('h3');
                    if (title) title.style.color = '';
                  }
                }}
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={p.hoverOverlayStyle}
                ></div>
                <div className="relative z-10 text-center">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300 border-2"
                    style={p.iconStyle}
                  >
                    <i className={`${p.icon} text-3xl`} style={{ color: p.iconStyle.color }}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 transition-colors">{p.title}</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">{p.desc}</p>
                  <div className="mt-6">
                    <span className="font-medium text-xs px-3 py-1 rounded-full" style={p.badgeStyle}>{p.badge}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sacred Journey Timeline ───────────────────── */}
      <section className="pt-12 pb-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-orange-300 rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 bg-yellow-300 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-red-300 rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
              <i className="fas fa-history text-orange-600 mr-3 text-lg"></i>
              <span className="text-orange-700 font-semibold">{t('aboutPage.timeline.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-800 mb-6">
              {t('aboutPage.timeline.title_1')} <span className="text-orange-500 font-bold">{t('aboutPage.timeline.title_2')}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('aboutPage.timeline.subtitle')}
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-orange-200 via-orange-300 to-orange-400"></div>

              <div className="space-y-8 md:space-y-16">
                {timeline.map((item) => {
                  const marker = (desktop = false) => (
                    <div
                      className={`about-timeline-dot${desktop ? ' about-timeline-dot--desktop' : ''}${item.animatePulse ? ' animate-pulse' : ''}`}
                      style={{ background: item.iconGradient }}
                      aria-hidden="true"
                    >
                      <i className={`${item.icon} text-white text-lg`}></i>
                    </div>
                  );

                  return (
                  <div key={item.key} className="relative flex flex-col md:flex-row md:items-center">
                    {marker(true)}
                    {item.left ? (
                      <>
                        <div className="w-full md:w-1/2 md:pr-12 md:text-right">
                          <div className={`rounded-2xl p-6 shadow-xl border ${item.border} hover:shadow-2xl transition-all duration-300 relative ${item.presentBg ? 'bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200' : 'bg-white'}`}>
                            <div className="md:hidden absolute top-4 right-4">
                              {marker()}
                            </div>
                            <h3
                              className="text-2xl font-bold mb-3 about-timeline-year"
                              style={{ backgroundImage: item.iconGradient }}
                            >
                              {item.year}
                            </h3>
                            <h4 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h4>
                            <p className="text-gray-700 leading-relaxed text-sm md:text-base">{item.desc}</p>
                            <div className="mt-4">
                              <span
                                className="inline-flex items-center text-sm font-medium about-timeline-year"
                                style={{ backgroundImage: item.iconGradient }}
                              >
                                <i className={`${item.badgeIcon} mr-2`}></i>{item.badge}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="hidden md:block w-1/2 pl-12" aria-hidden="true"></div>
                      </>
                    ) : (
                      <>
                        <div className="hidden md:block w-1/2 pr-12" aria-hidden="true"></div>
                        <div className="w-full md:w-1/2 md:pl-12">
                          <div className={`bg-white rounded-2xl p-6 shadow-xl border ${item.border} hover:shadow-2xl transition-all duration-300 relative`}>
                            <div className="md:hidden absolute top-4 right-4">
                              {marker()}
                            </div>
                            <h3
                              className="text-2xl font-bold mb-3 about-timeline-year"
                              style={{ backgroundImage: item.iconGradient }}
                            >
                              {item.year}
                            </h3>
                            <h4 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h4>
                            <p className="text-gray-700 leading-relaxed text-sm md:text-base">{item.desc}</p>
                            <div className="mt-4">
                              <span
                                className="inline-flex items-center text-sm font-medium about-timeline-year"
                                style={{ backgroundImage: item.iconGradient }}
                              >
                                <i className={`${item.badgeIcon} mr-2`}></i>{item.badge}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
